import type {
  ApiContext,
  ApiReturnFrontendPermission,
} from '@highimpact-frontend/api-types';
import {
  PermissionFetchProvider,
  type FetchFrontendPermission,
} from '@highimpact/dashboard-ui';
import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { DashboardScope } from '../state/dashboardRuntimeTypes';
import type { DashboardHostContextValue } from './DashboardHostContext.types';
import { buildDashboardScopeFromHostContext } from './DashboardHostContext.types';

// -- context --

const DashboardHostContext = createContext<DashboardHostContextValue | null>(
  null
);

function DashboardHostPermissionBridge({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const host = useDashboardHost();

  const fetchFrontendPermission: FetchFrontendPermission = async (
    params: ApiContext,
    signal: AbortSignal
  ): Promise<ApiReturnFrontendPermission> =>
    host.api.permissions.fetchFrontendPermission(params, signal);

  return (
    <PermissionFetchProvider fetchPermission={fetchFrontendPermission}>
      {children}
    </PermissionFetchProvider>
  );
}

export interface DashboardHostProviderProps extends PropsWithChildren {
  readonly value: DashboardHostContextValue;
}

/**
 * Supplies site, project, permissions, API clients, and remote widget rendering
 * from the host application into the dashboard library.
 */
export function DashboardHostProvider({
  value,
  children,
}: DashboardHostProviderProps): ReactElement {
  return (
    <DashboardHostContext.Provider value={value}>
      <DashboardHostPermissionBridge>{children}</DashboardHostPermissionBridge>
    </DashboardHostContext.Provider>
  );
}

/**
 * Reads the host context. Throws when the provider is missing.
 */
export function useDashboardHost(): DashboardHostContextValue {
  const hostContext = useContext(DashboardHostContext);

  if (hostContext === null) {
    throw new Error(
      'useDashboardHost must be used inside DashboardHostProvider'
    );
  }

  return hostContext;
}

/**
 * Returns project, site, and grid identifiers from the host context.
 */
export function useDashboardScope(): DashboardScope {
  const hostContext = useDashboardHost();

  return useMemo(
    () => buildDashboardScopeFromHostContext(hostContext),
    [hostContext]
  );
}
